// The seeded DE scorecard catalog (13 §DE scorecard catalog) as data. Zod-free on purpose
// (`@companyos/schema/de-metric-catalog`): the web bundle runs under a CSP without `unsafe-eval`,
// and zod probes `new Function` when a schema module loads, so the "Add digital employee"
// wizard reads the template KPIs from here, never from `@companyos/schema/contracts` (which
// re-exports all of this from `contracts/metric-catalog.ts`). Type imports only.
import type { z } from "zod";
import type { MetricTemplate } from "./contracts/api/measurement";
import type { EventQuery } from "./contracts/rows";

/** The event family every `time_entry.*` event folds into (see `EventQuery` in contracts/rows). */
export const TIME_ENTRY_FAMILY = "time_entry.*";

type Template = z.input<typeof MetricTemplate>;

const counter = {
  unit: "number",
  direction: "gte",
  aggregation: "cumulative",
  source: "event",
  formula: null,
  visibility: "client",
  tracking_mode: "scored",
} as const;

const event = (event_type: string, measure: EventQuery["measure"], filter?: EventQuery["filter"]) =>
  ({ event_type, measure, ...(filter ? { filter } : {}) }) as EventQuery;

/** AGENT-RECORD: `de.approval_rate` (migration 0095 moves the catalog's older formula to it). */
export const RECORD_APPROVAL_RATE =
  "(output.approved + review.approved + review.edited) / " +
  "(output.approved + output.rejected + review.approved + review.edited + review.rejected)";

const formula = (expr: string) =>
  ({ source: "formula", event_query: null, formula: expr }) as const;

/**
 * DE-KPIS-LIVE: the activity types of an ended run's outcome and of the memory calls CompanyOS
 * made for it (written only by the record's feeder; reserved at ingest below).
 */
export const RUN_OUTCOME_TYPES = {
  done: "run.done",
  failed: "run.failed",
  stopped: "run.stopped",
} as const;
export type RunOutcome = keyof typeof RUN_OUTCOME_TYPES;
export const MEMORY_USE_TYPES = { read: "memory.read", saved: "memory.saved" } as const;
/**
 * FAILURE-STOP (Andrei, 2026-11-02): an ended run's failures, from the MCP server's counters
 * (`run.failure_guard`): its tool errors (quantity = errors), a stop after repeated failures (one
 * per stopped run), and the tokens a failed or stopped run spent (quantity = tokens in + out).
 */
export const FAILURE_TYPES = {
  toolErrors: "run.tool_errors",
  failureStopped: "run.failure_stopped",
  failedTokens: "run.failed_tokens",
  /** TASK-BUDGETS: one per run a limit stopped (reservation, task cost, step cap, time limit). */
  budgetStopped: "run.budget_stopped",
} as const;
/** FAILURE-STOP: the seat metrics of those items (context: shown, not scored). */
export const FAILURE_METRIC_KEYS = [
  "de.tool_errors_per_run",
  "de.runs_failure_stopped",
  "de.runs_budget_stopped",
  "de.failure_rate",
  "de.failed_run_tokens",
] as const;
/**
 * DECISION-LEDGER (roadmap Phase 1 item 1; Andrei 2026-11-03): the three counters of an agent's
 * decision trail, one activity item each, written only by the record's feeder:
 *   decided    the agent policy let the agent act inside its run (an `agent_policy.decided` row
 *              with verdict `allow_in_run`);
 *   escalated  the agent asked a person: an approval it requested, a premise it escalated
 *              (`run.premise_escalated`), an `agent_escalation` Inbox item it raised;
 *   reversed   the agent's work was undone or thrown out because it was wrong: an undo of its
 *              write (`task.undone`), a send-back or reject of its delivered work marked wrong.
 *              An edit is never a reversal (it counts as `review.edited`).
 */
export const DECISION_TYPES = {
  decided: "decision.decided",
  escalated: "decision.escalated",
  reversed: "decision.reversed",
} as const;
/** DECISION-LEDGER: the seat metrics of those items (context: shown, not scored). */
export const DECISION_METRIC_KEYS = ["de.decisions", "de.escalations", "de.reversals"] as const;

/** Ended top-level runs, as a formula over the outcome items (0 when there were none). */
const RUN_TOTAL = "run.done + run.failed + run.stopped";

/** Every seeded DE metric. Keys in `RECRUITING_ONLY` are added by `template_key = recruiting_de`. */
export const DE_METRIC_CATALOG = [
  {
    ...counter,
    key: "de.activity_total",
    name: "Actions taken",
    cadence: "daily",
    event_query: event("activity.recorded", "count"),
    kpi_weight: 0,
    goal_weight: 0,
    tracking_mode: "context",
    default_weekly_target: null,
  },
  {
    ...counter,
    key: "de.tasks_completed",
    name: "Work done",
    cadence: "daily",
    event_query: event("activity.recorded", "count", { type: "task.completed" }),
    kpi_weight: 1,
    goal_weight: 0,
    default_weekly_target: 25,
  },
  {
    ...counter,
    key: "de.outputs_submitted",
    name: "Outputs for review",
    cadence: "daily",
    event_query: event("activity.recorded", "count", { type: "output.submitted" }),
    kpi_weight: 0,
    goal_weight: 0,
    tracking_mode: "context",
    default_weekly_target: null,
  },
  {
    ...counter,
    // AGENT-RECORD: the approvals decided in CompanyOS (`review.*`, the record feeder's; an
    // output approved after the approver edited it is still approved) join the outputs a source
    // reports as approved or rejected (`output.*`, as before); the as-is / edited / rejected
    // split of CompanyOS's own reviews is the three rates below.
    ...formula(RECORD_APPROVAL_RATE),
    key: "de.approval_rate",
    name: "Supervisor approval rate",
    unit: "percent",
    aggregation: "average",
    cadence: "weekly",
    kpi_weight: 1,
    goal_weight: 0,
    default_weekly_target: 0.9,
  },
  {
    ...counter,
    key: "de.outcomes",
    name: "Outcomes",
    cadence: "daily",
    event_query: event("outcome.recorded", "count"),
    kpi_weight: 2,
    goal_weight: 1,
    default_weekly_target: null,
  },
  {
    ...counter,
    key: "de.candidates_sourced",
    name: "Candidates sourced",
    cadence: "daily",
    event_query: event("outcome.recorded", "count", { type: "candidate.sourced" }),
    kpi_weight: 1,
    goal_weight: 0,
    default_weekly_target: 40,
  },
  {
    ...counter,
    key: "de.candidates_screened",
    name: "Candidates screened",
    cadence: "daily",
    event_query: event("outcome.recorded", "count", { type: "candidate.screened" }),
    kpi_weight: 1,
    goal_weight: 0,
    default_weekly_target: 20,
  },
  {
    ...counter,
    key: "de.interviews_booked",
    name: "Interviews booked",
    cadence: "weekly",
    event_query: event("outcome.recorded", "count", { type: "interview.booked" }),
    kpi_weight: 2,
    goal_weight: 1,
    default_weekly_target: 4,
  },
  {
    ...counter,
    key: "de.placements",
    name: "Placements",
    cadence: "monthly",
    event_query: event("outcome.recorded", "count", { type: "placement.made" }),
    kpi_weight: 2,
    goal_weight: 2,
    default_weekly_target: null,
  },
  {
    ...counter,
    key: "de.cost_total",
    name: "Cost",
    unit: "currency",
    direction: "lte",
    cadence: "daily",
    event_query: event("cost.posted", "sum:amount_minor", { payload: { visibility: "client" } }),
    kpi_weight: 1,
    goal_weight: 0,
    default_weekly_target: null,
  },
  {
    ...counter,
    key: "de.supervisor_hours",
    name: "Supervisor hours",
    unit: "time",
    direction: "lte",
    cadence: "weekly",
    event_query: event(TIME_ENTRY_FAMILY, "sum:duration_seconds", {
      payload: { kind: "supervision", approval_status: "approved" },
    }),
    kpi_weight: 1,
    goal_weight: 0,
    // 10 h. `time` values (targets included) are minutes in the engine (kpi/project.ts); the
    // event log's seconds are converted on projection (S1-18 found 36_000 here: 600 h).
    default_weekly_target: 600,
  },
  {
    ...counter,
    key: "de.founder_hours",
    name: "Founder hours",
    unit: "time",
    direction: "lte",
    cadence: "weekly",
    event_query: event(TIME_ENTRY_FAMILY, "sum:duration_seconds", {
      payload: { kind: "founder", approval_status: "approved" },
    }),
    visibility: "agency",
    kpi_weight: 1,
    goal_weight: 0,
    default_weekly_target: 300, // 5 h, in minutes (see de.supervisor_hours)
  },
  {
    ...counter,
    key: "de.cost_to_serve",
    name: "Cost to serve",
    unit: "currency",
    direction: "lte",
    cadence: "daily",
    event_query: event("cost.posted", "sum:amount_minor"),
    visibility: "agency",
    kpi_weight: 0,
    goal_weight: 0,
    tracking_mode: "context",
    default_weekly_target: null,
  },
  {
    ...counter,
    ...formula("de.cost_total / de.outcomes"),
    key: "de.cost_per_outcome",
    name: "Cost per outcome",
    unit: "currency",
    direction: "lte",
    aggregation: "average",
    cadence: "weekly",
    kpi_weight: 1,
    goal_weight: 0,
    default_weekly_target: null,
  },
  // AGENT-POLISH: the agent's own time, from consumer `run.agent_time` (one `agent.run` activity
  // per ended run: value = active minutes; services/api/src/domain/runs/agent-time.ts). Context,
  // not scored, until the founder sets targets for agent time.
  {
    ...counter,
    key: "de.agent_hours",
    name: "Agent hours",
    unit: "time",
    cadence: "weekly",
    event_query: event("activity.recorded", "sum:value", { type: "agent.run" }),
    kpi_weight: 0,
    goal_weight: 0,
    tracking_mode: "context",
    default_weekly_target: null,
  },
  {
    ...counter,
    key: "de.runs_completed",
    name: "Runs completed",
    cadence: "weekly",
    event_query: event("activity.recorded", "count", {
      type: "agent.run",
      attrs: { state: "completed" },
    }),
    kpi_weight: 0,
    goal_weight: 0,
    tracking_mode: "context",
    default_weekly_target: null,
  },
  {
    ...counter,
    ...formula("de.supervisor_hours / de.outcomes"),
    key: "de.hours_per_outcome",
    name: "Supervisor hours per outcome",
    unit: "time",
    direction: "lte",
    aggregation: "average",
    cadence: "weekly",
    kpi_weight: 0,
    goal_weight: 0,
    tracking_mode: "context",
    default_weekly_target: null,
  },
  // AGENT-RECORD (docs/direction/2026-10-28-managing-agents-gaps.md §2.1): the agent record's
  // review split and interventions, fed by consumer `agent_record.feed` from CompanyOS's own
  // approvals, take-overs and cancels (services/api/src/domain/agent-record/feed.ts; `review.*`
  // and the rest are reserved to it at ingest). Context, not scored, until the founder sets
  // targets for them.
  {
    ...counter,
    ...formula("review.approved / (review.approved + review.edited + review.rejected)"),
    key: "de.approved_as_is_rate",
    name: "Approved as-is",
    unit: "percent",
    aggregation: "average",
    cadence: "weekly",
    kpi_weight: 0,
    goal_weight: 0,
    tracking_mode: "context",
    default_weekly_target: null,
  },
  {
    ...counter,
    ...formula("review.edited / (review.approved + review.edited + review.rejected)"),
    key: "de.edited_rate",
    name: "Edited before approval",
    unit: "percent",
    direction: "lte",
    aggregation: "average",
    cadence: "weekly",
    kpi_weight: 0,
    goal_weight: 0,
    tracking_mode: "context",
    default_weekly_target: null,
  },
  {
    ...counter,
    ...formula("review.rejected / (review.approved + review.edited + review.rejected)"),
    key: "de.rejected_rate",
    name: "Rejected",
    unit: "percent",
    direction: "lte",
    aggregation: "average",
    cadence: "weekly",
    kpi_weight: 0,
    goal_weight: 0,
    tracking_mode: "context",
    default_weekly_target: null,
  },
  {
    ...counter,
    key: "de.interventions",
    name: "Interventions",
    direction: "lte",
    cadence: "weekly",
    event_query: event("activity.recorded", "count", { type: "intervention.recorded" }),
    kpi_weight: 0,
    goal_weight: 0,
    tracking_mode: "context",
    default_weekly_target: null,
  },
  // The record's outcome, once per piece of work: a task the DE finished, or its output a person
  // accepted (as-is or after an edit) that belongs to no task (an approval of a task's work is a
  // review; the task's completion is its outcome). The record's feeder writes one `work.outcome`
  // item per outcome. Business outcomes it reports (`de.outcomes`) are kept apart.
  {
    ...counter,
    key: "de.work_outcomes",
    event_query: event("activity.recorded", "count", { type: "work.outcome" }),
    name: "Work outcomes",
    cadence: "weekly",
    kpi_weight: 0,
    goal_weight: 0,
    tracking_mode: "context",
    default_weekly_target: null,
  },
  // DECISION-LEDGER: decided / escalated / reversed per week (see `DECISION_TYPES`).
  {
    ...counter,
    key: "de.decisions",
    name: "Decided in run",
    cadence: "weekly",
    event_query: event("activity.recorded", "count", { type: DECISION_TYPES.decided }),
    kpi_weight: 0,
    goal_weight: 0,
    tracking_mode: "context",
    default_weekly_target: null,
  },
  {
    ...counter,
    key: "de.escalations",
    name: "Escalated",
    direction: "lte",
    cadence: "weekly",
    event_query: event("activity.recorded", "count", { type: DECISION_TYPES.escalated }),
    kpi_weight: 0,
    goal_weight: 0,
    tracking_mode: "context",
    default_weekly_target: null,
  },
  {
    ...counter,
    key: "de.reversals",
    name: "Reversed",
    direction: "lte",
    cadence: "weekly",
    event_query: event("activity.recorded", "count", { type: DECISION_TYPES.reversed }),
    kpi_weight: 0,
    goal_weight: 0,
    tracking_mode: "context",
    default_weekly_target: null,
  },
  {
    ...counter,
    ...formula("de.cost_total / de.work_outcomes"),
    key: "de.cost_per_work_outcome",
    // The seat's measure (its cost and outcomes in the period, whoever held it); the Record tab
    // is the DE's own (its owned cost, its outcomes, while it held the seat).
    name: "Seat cost per work outcome",
    unit: "currency",
    direction: "lte",
    aggregation: "average",
    cadence: "weekly",
    kpi_weight: 0,
    goal_weight: 0,
    tracking_mode: "context",
    default_weekly_target: null,
  },
  {
    ...counter,
    ...formula("de.supervisor_hours / de.work_outcomes"),
    key: "de.hours_per_work_outcome",
    name: "Seat supervisor hours per work outcome",
    unit: "time",
    direction: "lte",
    aggregation: "average",
    cadence: "weekly",
    kpi_weight: 0,
    goal_weight: 0,
    tracking_mode: "context",
    default_weekly_target: null,
  },
  // DE-KPIS-LIVE (docs/design/2026-10-31-de-kpis-live.md): the operating KPIs every DE gets, fed
  // by the record's feeder from its own runs (one `run.done` / `run.failed` / `run.stopped` item
  // per ended top-level run) and from the memory calls CompanyOS made for that run (`memory.read`,
  // `memory.saved`, quantity = calls). Context: shown and mirrored, not in the KPI score until a
  // manager weights them.
  {
    ...counter,
    key: "de.runs_done",
    name: "Runs done",
    cadence: "weekly",
    event_query: event("activity.recorded", "count", { type: "run.done" }),
    kpi_weight: 0,
    goal_weight: 0,
    tracking_mode: "context",
    default_weekly_target: null,
  },
  {
    ...counter,
    key: "de.runs_failed",
    name: "Runs failed or blocked",
    direction: "lte",
    cadence: "weekly",
    event_query: event("activity.recorded", "count", { type: "run.failed" }),
    kpi_weight: 0,
    goal_weight: 0,
    tracking_mode: "context",
    default_weekly_target: null,
  },
  {
    ...counter,
    key: "de.runs_stopped",
    name: "Runs stopped early",
    direction: "lte",
    cadence: "weekly",
    event_query: event("activity.recorded", "count", { type: "run.stopped" }),
    kpi_weight: 0,
    goal_weight: 0,
    tracking_mode: "context",
    default_weekly_target: null,
  },
  {
    ...counter,
    ...formula(RUN_TOTAL),
    key: "de.runs",
    name: "Runs",
    cadence: "weekly",
    kpi_weight: 0,
    goal_weight: 0,
    tracking_mode: "context",
    default_weekly_target: null,
  },
  {
    ...counter,
    ...formula(`run.done / (${RUN_TOTAL})`),
    key: "de.run_success_rate",
    name: "Run success rate",
    unit: "percent",
    aggregation: "average",
    cadence: "weekly",
    kpi_weight: 0,
    goal_weight: 0,
    tracking_mode: "context",
    // The platform's default, so health can go off; each seat may set its own.
    default_weekly_target: 0.9,
  },
  {
    ...counter,
    ...formula(`de.cost_total / (${RUN_TOTAL})`),
    key: "de.cost_per_run",
    // The seat's measure (its cost in the period over its DE's runs); the Record's is the DE's own.
    name: "Seat cost per run",
    unit: "currency",
    direction: "lte",
    aggregation: "average",
    cadence: "weekly",
    kpi_weight: 0,
    goal_weight: 0,
    tracking_mode: "context",
    default_weekly_target: null,
  },
  // FAILURE-STOP: the failure KPIs (context, weight 0: shown, not in the score yet).
  {
    ...counter,
    key: "de.tool_errors",
    name: "Tool errors",
    direction: "lte",
    cadence: "weekly",
    event_query: event("activity.recorded", "sum:quantity", { type: "run.tool_errors" }),
    kpi_weight: 0,
    goal_weight: 0,
    tracking_mode: "context",
    default_weekly_target: null,
  },
  {
    ...counter,
    ...formula(`de.tool_errors / (${RUN_TOTAL})`),
    key: "de.tool_errors_per_run",
    name: "Tool errors per run",
    direction: "lte",
    aggregation: "average",
    cadence: "weekly",
    kpi_weight: 0,
    goal_weight: 0,
    tracking_mode: "context",
    default_weekly_target: null,
  },
  {
    ...counter,
    key: "de.runs_failure_stopped",
    name: "Runs stopped after repeated failures",
    direction: "lte",
    cadence: "weekly",
    event_query: event("activity.recorded", "count", { type: "run.failure_stopped" }),
    kpi_weight: 0,
    goal_weight: 0,
    tracking_mode: "context",
    default_weekly_target: null,
  },
  // TASK-BUDGETS: runs a hard limit stopped (steps, cost, time) — context, not scored.
  {
    ...counter,
    key: "de.runs_budget_stopped",
    name: "Runs stopped by budget",
    direction: "lte",
    cadence: "weekly",
    event_query: event("activity.recorded", "count", { type: "run.budget_stopped" }),
    kpi_weight: 0,
    goal_weight: 0,
    tracking_mode: "context",
    default_weekly_target: null,
  },
  {
    ...counter,
    ...formula(`(run.failed + run.stopped) / (${RUN_TOTAL})`),
    key: "de.failure_rate",
    name: "Failure rate",
    unit: "percent",
    direction: "lte",
    aggregation: "average",
    cadence: "weekly",
    kpi_weight: 0,
    goal_weight: 0,
    tracking_mode: "context",
    default_weekly_target: null,
  },
  {
    ...counter,
    key: "de.failed_run_tokens",
    name: "Tokens in failed or stopped runs",
    direction: "lte",
    cadence: "weekly",
    event_query: event("activity.recorded", "sum:quantity", { type: "run.failed_tokens" }),
    kpi_weight: 0,
    goal_weight: 0,
    tracking_mode: "context",
    default_weekly_target: null,
  },
  {
    ...counter,
    key: "de.memory_reads",
    name: "Memory reads",
    cadence: "weekly",
    event_query: event("activity.recorded", "sum:quantity", { type: "memory.read" }),
    kpi_weight: 0,
    goal_weight: 0,
    tracking_mode: "context",
    default_weekly_target: null,
  },
  {
    ...counter,
    key: "de.memory_saves",
    name: "Memory saves",
    cadence: "weekly",
    event_query: event("activity.recorded", "sum:quantity", { type: "memory.saved" }),
    kpi_weight: 0,
    goal_weight: 0,
    tracking_mode: "context",
    default_weekly_target: null,
  },
] as const satisfies readonly Template[];

/**
 * TEMPLATES-T2 (agent-templates design §4, §5; agent-types §2): the KPIs of the role templates
 * and the skills that feed them. The DE reports each one itself (`source = manual`: the
 * `outcomes.record` tool's value, e.g. from `weekly-kpi-report`), weekly. They are not part of the
 * company catalog: a definition is written into a company only when a DE is deployed from a
 * role that names it (domain/templates/deploy.ts), so nothing appears in a company that never
 * used a role. Targets here are the catalog's; a role, a vertical pack or a client overlay may
 * set its own.
 */
const reported = (
  key: string,
  name: string,
  unit: "number" | "percent",
  direction: "gte" | "lte",
  target: number | null,
  aggregation: "cumulative" | "average" = unit === "percent" ? "average" : "cumulative",
) =>
  ({
    key,
    name,
    unit,
    direction,
    aggregation,
    cadence: "weekly",
    source: "manual",
    event_query: null,
    formula: null,
    visibility: "client",
    tracking_mode: "scored",
    kpi_weight: 1,
    goal_weight: 0,
    default_weekly_target: target,
  }) as const satisfies Template;

export const ROLE_METRIC_CATALOG = [
  // mail
  reported("role.inbox_zero_rate", "Inbox zero by 17:00", "percent", "gte", 90),
  reported(
    "role.triage_latency_minutes",
    "Triage latency (minutes)",
    "number",
    "lte",
    null,
    "average",
  ),
  reported("role.reply_approval_rate", "Reply approval rate", "percent", "gte", 80),
  reported("role.replies_sent", "Replies sent", "number", "gte", null),
  reported("role.followups_on_time_rate", "Follow-ups on time", "percent", "gte", 100),
  // calendar and meetings
  reported("role.conflicts_caught", "Conflicts caught", "number", "gte", null),
  reported("role.conflicts_shipped", "Conflicts shipped", "number", "lte", 0),
  reported("role.meetings_scheduled", "Meetings scheduled", "number", "gte", null),
  reported("role.time_to_schedule_days", "Time to schedule (days)", "number", "lte", 1, "average"),
  reported("role.prereads_on_time_rate", "Pre-reads on time", "percent", "gte", 100),
  reported("role.actions_captured_rate", "Meeting actions captured", "percent", "gte", 100),
  // messaging and intake
  reported(
    "role.text_response_minutes",
    "Text response time (minutes)",
    "number",
    "lte",
    15,
    "average",
  ),
  reported(
    "role.first_reply_minutes",
    "Time to first reply (minutes)",
    "number",
    "lte",
    30,
    "average",
  ),
  reported("role.handoff_rate", "Hand-off rate", "percent", "lte", null),
  reported("role.reopen_rate", "Reopen rate", "percent", "lte", null),
  // board, docs, research
  reported("role.tasks_captured_same_day_rate", "Tasks captured same day", "percent", "gte", 95),
  reported("role.cards_complete_rate", "Cards complete at creation", "percent", "gte", 100),
  reported("role.docs_on_time_rate", "Docs on time", "percent", "gte", 100),
  reported("role.first_time_approval_rate", "First-time approval", "percent", "gte", 70),
  reported("role.briefs_on_time_rate", "Briefs on time", "percent", "gte", 100),
  reported("role.unverified_claims", "Unverified claims", "number", "lte", 0),
  // CRM and leads
  reported("role.crm_duplicate_rate", "CRM duplicates", "percent", "lte", 2),
  reported("role.qualified_leads", "Qualified leads", "number", "gte", null),
  reported("role.lead_gate_failures", "Lead gate failures", "number", "lte", null),
  reported("role.prospect_replies", "Prospect replies", "number", "gte", null),
  reported("role.meetings_booked", "Meetings booked", "number", "gte", null),
  // money
  reported(
    "role.invoice_chase_days",
    "Days to chase an overdue invoice",
    "number",
    "lte",
    3,
    "average",
  ),
  reported("role.receipts_matched_rate", "Receipts matched", "percent", "gte", 95),
  reported("role.unmatched_flagged_rate", "Unmatched items flagged weekly", "percent", "gte", 100),
  // social
  reported("role.posts_ready_rate", "Weekly posts ready", "percent", "gte", 100),
  reported("role.parked_rate", "Posts parked", "percent", "lte", 20),
  // cadence and coordination
  reported("role.standup_on_time_rate", "Standup on time", "percent", "gte", 100),
  reported("role.report_on_time_rate", "Weekly report on time", "percent", "gte", 100),
  reported("role.escalations_in_sla_rate", "Escalations resolved in SLA", "percent", "gte", 90),
  reported("role.risks_flagged_early_rate", "Risks flagged early", "percent", "gte", 90),
  // recruiting: `de.interviews_booked` and `de.candidates_screened` of the DE catalog serve
  // knowledge steward (agent-types §2)
  reported("role.open_contradictions", "Open contradictions", "number", "lte", null, "average"),
  reported(
    "role.settlement_age_days",
    "Median settlement age (days)",
    "number",
    "lte",
    7,
    "average",
  ),
  reported("role.brief_age_days", "Company brief age (days)", "number", "lte", 7, "average"),
  reported("role.stale_facts_found", "Stale facts found", "number", "gte", null),
  reported("role.answer_accuracy_rate", "Answer accuracy", "percent", "gte", 90),
] as const;
export type RoleMetric = (typeof ROLE_METRIC_CATALOG)[number];
export const ROLE_METRIC_KEYS: ReadonlySet<string> = new Set(ROLE_METRIC_CATALOG.map((m) => m.key));
export const roleMetric = (key: string): RoleMetric | undefined =>
  ROLE_METRIC_CATALOG.find((m) => m.key === key);

/**
 * DE-KPIS-LIVE: the activity types of a DE's outcomes that CompanyOS sees happen (written only by
 * the record's feeder; reserved at ingest): a request it delivered through its helpers (a helper
 * chain its run closed as delivered) and the minutes from the request to that delivery, a message
 * it posted, a brief it sent, an issue it resolved, a meeting it scheduled.
 */
export const OUTCOME_ITEM_TYPES = {
  delivered: "request.delivered",
  deliveryMinutes: "request.delivery_minutes",
  message: "message.posted",
  brief: "brief.sent",
  issueClosed: "issue.closed",
  meeting: "meeting.booked",
} as const;

/**
 * CAROUSEL-KPI: one carousel the DE's bridge saw delivered (a finished job whose delivery
 * settled), posted as an `activity` item by the bridge with the DE's own credential, whether
 * monitoring is on or off: item_key `carousel:<job_id>`, attrs `job_id`, `slides`, `cost_usd`.
 * Not reserved: the DE's own key may post it. See infra/ops/de-lab-bridge.py v2.8.2.
 */
export const CAROUSEL_DELIVERED_TYPE = "carousel.delivered";

const outcomeCount = (key: string, name: string, type: string) =>
  ({
    ...counter,
    key,
    name,
    cadence: "weekly",
    event_query: event("activity.recorded", "count", { type }),
    kpi_weight: 0,
    goal_weight: 0,
    tracking_mode: "context",
    default_weekly_target: null,
  }) as const satisfies Template;

/**
 * DE-KPIS-LIVE: the outcome KPIs CompanyOS can feed from what it sees happen (the role defaults
 * below pick from these, the DE catalog and the reported role metrics). Context on the seat until
 * a manager weights them; counts carry no default target (a team's volume is its own), a time or
 * a rate does where the platform has a sane one.
 */
export const DE_OUTCOME_CATALOG = [
  outcomeCount("de.requests_delivered", "Requests delivered", OUTCOME_ITEM_TYPES.delivered),
  {
    ...counter,
    key: "de.delivery_minutes",
    name: "Request-to-delivery minutes",
    cadence: "weekly",
    event_query: event("activity.recorded", "sum:quantity", {
      type: OUTCOME_ITEM_TYPES.deliveryMinutes,
    }),
    kpi_weight: 0,
    goal_weight: 0,
    tracking_mode: "context",
    default_weekly_target: null,
  },
  {
    ...counter,
    ...formula("de.delivery_minutes / de.requests_delivered / 60"),
    key: "de.delivery_hours",
    name: "Hours from request to delivery",
    direction: "lte",
    aggregation: "average",
    cadence: "weekly",
    kpi_weight: 0,
    goal_weight: 0,
    tracking_mode: "context",
    default_weekly_target: null,
  },
  outcomeCount("de.messages_posted", "Messages posted", OUTCOME_ITEM_TYPES.message),
  outcomeCount("de.briefs_sent", "Briefs sent", OUTCOME_ITEM_TYPES.brief),
  outcomeCount("de.issues_closed", "Issues closed", OUTCOME_ITEM_TYPES.issueClosed),
  outcomeCount("de.meetings_booked", "Meetings booked", OUTCOME_ITEM_TYPES.meeting),
  // CAROUSEL-KPI: fed by the bridge's `carousel.delivered` item (CAROUSEL_DELIVERED_TYPE).
  outcomeCount("de.carousels_delivered", "Carousels delivered", CAROUSEL_DELIVERED_TYPE),
  {
    ...counter,
    // The spend ledger (`de.cost_total`: client-visible cost.posted) over the carousels delivered.
    ...formula("de.cost_total / de.carousels_delivered"),
    key: "de.cost_per_carousel",
    name: "Cost per carousel",
    unit: "currency",
    direction: "lte",
    aggregation: "average",
    cadence: "weekly",
    kpi_weight: 0,
    goal_weight: 0,
    tracking_mode: "context",
    default_weekly_target: null,
  },
] as const satisfies readonly Template[];
/** Definitions an outcome KPI's formula reads, written with it (not shown as outcomes). */
export const OUTCOME_DEPENDS: Readonly<Record<string, readonly string[]>> = {
  "de.delivery_hours": ["de.delivery_minutes", "de.requests_delivered"],
  "de.cost_per_carousel": ["de.cost_total", "de.carousels_delivered"],
};

/** One outcome KPI of a role: a catalog key, the label the role reads it by, its own target. */
export type RoleOutcome = { key: string; label?: string; target?: number };

/**
 * DE-KPIS-LIVE: each role's 2–4 outcome KPIs, the platform's defaults for every company (a seat
 * may drop, add, weight or re-target them). Keys are the DE catalog's and `DE_OUTCOME_CATALOG`'s
 * (fed from events CompanyOS sees) and the reported role metrics (`role.*`: shown as not tracked
 * until the DE reports them). `default` serves a DE whose role matches none.
 */
export const ROLE_OUTCOME_KPIS = {
  "chief-of-staff": [
    { key: "de.briefs_sent" },
    { key: "de.issues_closed" },
    { key: "role.followups_on_time_rate", label: "Follow-ups closed on time" },
  ],
  marketing: [
    { key: "de.requests_delivered", label: "Posts delivered" },
    { key: "de.approved_as_is_rate", target: 0.8 },
    { key: "de.delivery_hours", label: "Time from request to delivery (h)" },
  ],
  "carousel-studio": [
    { key: "de.carousels_delivered" },
    { key: "de.cost_per_carousel" },
    { key: "de.approved_as_is_rate", target: 0.8 },
  ],
  "social-media-publisher": [
    { key: "de.requests_delivered", label: "Posts delivered" },
    { key: "de.approved_as_is_rate", target: 0.8 },
    { key: "role.posts_ready_rate" },
  ],
  "executive-assistant": [
    { key: "de.meetings_booked" },
    { key: "de.messages_posted", label: "Replies and updates sent" },
    { key: "role.followups_on_time_rate" },
  ],
  "admin-assistant": [
    { key: "de.tasks_completed" },
    { key: "de.meetings_booked" },
    { key: "de.approved_as_is_rate", target: 0.8 },
  ],
  "sdr-lead-research": [
    { key: "role.qualified_leads" },
    { key: "de.meetings_booked" },
    { key: "role.prospect_replies" },
  ],
  "customer-support-first-line": [
    { key: "de.messages_posted", label: "Replies sent" },
    { key: "role.first_reply_minutes" },
    { key: "role.reopen_rate" },
  ],
  "bookkeeper-assistant": [
    { key: "role.receipts_matched_rate" },
    { key: "role.invoice_chase_days" },
    { key: "de.tasks_completed" },
  ],
  recruiting: [
    { key: "de.candidates_screened" },
    { key: "de.interviews_booked" },
    { key: "de.placements" },
  ],
  "knowledge-steward": [
    { key: "role.answer_accuracy_rate" },
    { key: "role.open_contradictions" },
    { key: "de.tasks_completed" },
  ],
  default: [
    { key: "de.tasks_completed" },
    { key: "de.approved_as_is_rate", target: 0.8 },
    { key: "de.requests_delivered" },
  ],
} as const satisfies Record<string, readonly RoleOutcome[]>;
export type OutcomeRole = keyof typeof ROLE_OUTCOME_KPIS;

/** Template keys that are a role under another name (a vertical's or an older key). */
const ROLE_ALIASES: Readonly<Record<string, OutcomeRole>> = {
  recruiting_de: "recruiting",
  recruiter: "recruiting",
  "recruiter-coordinator": "recruiting",
  bookkeeper: "bookkeeper-assistant",
  "content-studio": "marketing",
  writer: "marketing",
};

/** Words in a role's name (its seat title or template key) that say which role it is. */
const ROLE_WORDS: readonly [RegExp, OutcomeRole][] = [
  [/chief of staff|\bcos\b|chief-of-staff/, "chief-of-staff"],
  [/carousel/, "carousel-studio"],
  [/social/, "social-media-publisher"],
  [/marketing|content|copy|creative|brand|growth/, "marketing"],
  [/executive assistant|executive-assistant|\bea\b/, "executive-assistant"],
  [/admin|assistant|office/, "admin-assistant"],
  [/sdr|sales|lead gen|prospect|business development/, "sdr-lead-research"],
  [
    /support|customer|success|account health|account manag|help ?desk/,
    "customer-support-first-line",
  ],
  [
    /book ?keep|finance|accounting|accounts (payable|receivable)|invoice|controller/,
    "bookkeeper-assistant",
  ],
  [/recruit|talent|sourc|hiring/, "recruiting"],
  [/knowledge|steward|librarian|research/, "knowledge-steward"],
];

/**
 * DE-KPIS-LIVE: which role's outcome KPIs a DE gets: its role template's, else the role its seat
 * title names, else an older or vertical key's (`recruiting_de` was the default template of every
 * early DE, so a seat title saying otherwise wins over it), else the words of the key, else
 * `default`.
 */
export function outcomeRoleOf(templateKey: string | null, title: string | null): OutcomeRole {
  const key = (templateKey ?? "").toLowerCase();
  if (Object.hasOwn(ROLE_OUTCOME_KPIS, key) && key !== "default") return key as OutcomeRole;
  const named = (text: string) => ROLE_WORDS.find(([re]) => re.test(text))?.[1];
  return named((title ?? "").toLowerCase()) ?? ROLE_ALIASES[key] ?? named(key) ?? "default";
}

/** A role's outcome KPIs. */
export const roleOutcomes = (role: OutcomeRole): readonly RoleOutcome[] => ROLE_OUTCOME_KPIS[role];

/** Every key an outcome KPI may name (to find a seat's outcome rows). */
export const OUTCOME_KEYS: ReadonlySet<string> = new Set(
  Object.values(ROLE_OUTCOME_KPIS).flatMap((r) => r.map((o) => o.key)),
);

/**
 * AGENT-RECORD: the record's metrics, seeded on every DE's seat even when its KPIs are left to the
 * job (`seed_template_metrics: false`), as context; `RECORD_METRIC_DEPENDS` are the definitions
 * their formulas read (written to the company, not put on the seat).
 */
export const RECORD_METRIC_KEYS = [
  "de.approval_rate",
  "de.approved_as_is_rate",
  "de.edited_rate",
  "de.rejected_rate",
  "de.interventions",
  "de.work_outcomes",
  "de.cost_per_work_outcome",
  "de.hours_per_work_outcome",
  // DE-KPIS-LIVE: the operating KPIs (runs, success, cost per run, memory use)
  "de.runs",
  "de.runs_done",
  "de.runs_failed",
  "de.runs_stopped",
  "de.run_success_rate",
  "de.cost_per_run",
  "de.memory_reads",
  "de.memory_saves",
  // FAILURE-STOP
  "de.tool_errors",
  ...FAILURE_METRIC_KEYS,
  // DECISION-LEDGER
  ...DECISION_METRIC_KEYS,
] as const;
export const RECORD_METRIC_DEPENDS = ["de.cost_total", "de.supervisor_hours"] as const;
/**
 * DE-KPIS-LIVE: the spend KPI every DE's seat carries with the record (`spend.budget_used`, fed by
 * the `spend.snapshot` job from the ledger), as context.
 */
export const RECORD_SPEND_KEYS = ["spend.budget_used"] as const;

/**
 * AGENT-RECORD: the activity types only CompanyOS's record feeder writes (from approvals,
 * completions and take-overs); ingest refuses them from anyone else, and no agent reports them.
 */
export const RECORD_RESERVED_TYPES: readonly string[] = [
  "review.approved",
  "review.edited",
  "review.rejected",
  "work.outcome",
  "intervention.recorded",
  // DE-KPIS-LIVE: a run's outcome and its memory calls, and AGENT-POLISH's run time (consumer
  // `run.agent_time`), are CompanyOS's word on its own runs
  RUN_OUTCOME_TYPES.done,
  RUN_OUTCOME_TYPES.failed,
  RUN_OUTCOME_TYPES.stopped,
  MEMORY_USE_TYPES.read,
  MEMORY_USE_TYPES.saved,
  // FAILURE-STOP: a run's failures are CompanyOS's count
  ...Object.values(FAILURE_TYPES),
  "agent.run",
  // DE-KPIS-LIVE: the outcomes CompanyOS sees happen (deliveries, messages, briefs, issues,
  // meetings) are its word too
  ...Object.values(OUTCOME_ITEM_TYPES),
  // DECISION-LEDGER: decided / escalated / reversed are CompanyOS's count of its own gates
  ...Object.values(DECISION_TYPES),
];
/** The record feeder's item keys (`record:{what}:{id}`): no one else posts under them. */
export const RECORD_ITEM_KEY_PREFIX = "record:";

export const RECRUITING_ONLY = [
  "de.candidates_sourced",
  "de.candidates_screened",
  "de.interviews_booked",
  "de.placements",
] as const;

/**
 * AGENT-RECORD: the metric set every DE gets (the catalog without the recruiting outcomes): its
 * work, its review record, its cost and its supervisor's time (gaps doc §2.1 "seed the DE metric
 * set for every DE template").
 */
export const DE_BASE_METRICS: readonly (typeof DE_METRIC_CATALOG)[number][] =
  DE_METRIC_CATALOG.filter((m) => !(RECRUITING_ONLY as readonly string[]).includes(m.key));

/**
 * Template key → the KPIs it seeds on its DE's seat, in catalog order. 13 names one template,
 * `recruiting_de`, which carries the whole catalog; AGENT-RECORD: every other key seeds the base
 * set (`deTemplateMetrics`). Shared by the API's DE creation and the "Add digital employee"
 * wizard's KPI step (DE-WIZARD).
 */
export const DE_TEMPLATE_METRICS: Readonly<
  Record<string, readonly (typeof DE_METRIC_CATALOG)[number][]>
> = {
  recruiting_de: DE_METRIC_CATALOG,
};

/**
 * SPEND-KPIS: the agent-spend metrics (source `ledger`: the `spend.snapshot` job writes their
 * values from the cost ledger's agent rows; nobody enters them). Bind one to a seat (that seat's
 * agents), a team or the company (Scorecard/board). Currency values are minor units.
 */
const spend = {
  unit: "currency",
  direction: "lte",
  aggregation: "cumulative",
  source: "ledger",
  event_query: null,
  formula: null,
  visibility: "client",
  kpi_weight: 0,
  goal_weight: 0,
  tracking_mode: "context",
  default_weekly_target: null,
} as const;

export const SPEND_METRIC_CATALOG = [
  { ...spend, key: "spend.agent_total", name: "Agent spend", cadence: "daily" },
  { ...spend, key: "spend.agent_week", name: "Agent spend (week)", cadence: "weekly" },
  {
    ...spend,
    key: "spend.avg_per_day",
    name: "Agent spend per day (avg)",
    cadence: "weekly",
    aggregation: "average",
  },
  {
    ...spend,
    key: "spend.cost_per_task",
    name: "Agent spend per done task",
    cadence: "weekly",
    aggregation: "average",
  },
  {
    ...spend,
    key: "spend.budget_used",
    name: "DE spend vs budget",
    unit: "percent",
    cadence: "monthly",
    aggregation: "average",
    kpi_weight: 1,
    tracking_mode: "scored",
    default_weekly_target: 100,
  },
] as const satisfies readonly Template[];

export type SpendMetricKey = (typeof SPEND_METRIC_CATALOG)[number]["key"];
export const SPEND_METRIC_KEYS: ReadonlySet<string> = new Set(
  SPEND_METRIC_CATALOG.map((m) => m.key),
);

/**
 * BIZ-CLIENT-HEALTH: the client-health KPIs (source `ledger` too: system-fed, never typed in; the
 * daily `client_health.snapshot` job writes them from the clients' health scores). On the
 * company: over every live client space; on a seat: over the clients whose account seat it is.
 */
const clientHealth = {
  unit: "number",
  aggregation: "average",
  source: "ledger",
  event_query: null,
  formula: null,
  visibility: "client",
  kpi_weight: 1,
  goal_weight: 0,
  tracking_mode: "scored",
  cadence: "weekly",
} as const;

export const CLIENT_METRIC_CATALOG = [
  // the average health score (0–100): late and reopened work, open client issues, first-reply
  // time and NPS/CSAT answers
  {
    ...clientHealth,
    key: "client.health",
    name: "Client health (avg)",
    direction: "gte",
    default_weekly_target: 75,
  },
  // clients whose health is red (below 50)
  {
    ...clientHealth,
    key: "client.at_risk",
    name: "Clients at risk",
    direction: "lte",
    default_weekly_target: 0,
  },
  // NPS over the answered NPS surveys of the last 90 days (−100 to 100)
  {
    ...clientHealth,
    key: "client.nps",
    name: "Client NPS (90 days)",
    direction: "gte",
    tracking_mode: "context",
    kpi_weight: 0,
    default_weekly_target: null,
  },
] as const satisfies readonly Template[];

export type ClientMetricKey = (typeof CLIENT_METRIC_CATALOG)[number]["key"];
export const CLIENT_METRIC_KEYS: ReadonlySet<string> = new Set(
  CLIENT_METRIC_CATALOG.map((m) => m.key),
);

/**
 * T3-TASK-ROLLUPS: task-metric templates (source `task`: the `task_metric.snapshot` job counts
 * them from a board's tasks; nobody enters them). Using one picks the board, and the number
 * field for a sum or an average; the template gives the measure and the day a task counts on.
 * Keys here are the template's; a metric made from one gets its own key.
 */
const taskMetric = {
  direction: "gte",
  source: "task",
  event_query: null,
  formula: null,
  visibility: "client",
  kpi_weight: 1,
  goal_weight: 0,
  tracking_mode: "scored",
  default_weekly_target: null,
} as const;

export const TASK_METRIC_CATALOG = [
  {
    ...taskMetric,
    key: "tasks.done_per_week",
    name: "Tasks done per week",
    unit: "number",
    aggregation: "cumulative",
    cadence: "weekly",
    task_shape: { measure: "count", date: "completed_at", filter_done: true },
  },
  {
    ...taskMetric,
    key: "tasks.sum_per_month",
    name: "Sum of a number per month",
    unit: "number",
    aggregation: "cumulative",
    cadence: "monthly",
    task_shape: { measure: "sum", date: "completed_at", filter_done: true },
  },
  {
    ...taskMetric,
    key: "tasks.avg_per_week",
    name: "Average of a number per week",
    unit: "number",
    aggregation: "average",
    cadence: "weekly",
    task_shape: { measure: "avg", date: "completed_at", filter_done: true },
  },
] as const satisfies readonly Template[];

/** The metric set a DE created from `templateKey` gets (the base set unless the key names one). */
export const deTemplateMetrics = (
  templateKey: string,
): readonly (typeof DE_METRIC_CATALOG)[number][] =>
  Object.hasOwn(DE_TEMPLATE_METRICS, templateKey)
    ? (DE_TEMPLATE_METRICS[templateKey] as readonly (typeof DE_METRIC_CATALOG)[number][])
    : DE_BASE_METRICS;
